import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import Popper from "@mui/material/Popper";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { withRouter } from "react-router-dom";

import { useStateValue } from "../../Contexto/store";
import { useBusquedaGlobal, useRecientes } from "./hooks";
import RecentSearches from "./RecentSearches";
import ResultadoLista from "./ResultadoLista";
import NoResults from "./NoResults";
import { useBuscadorGlobalStyles } from "./styles/BuscadorGlobalStyles";

const BuscadorGlobal = ({ history, labels: labelsProps }) => {
  const classes = useBuscadorGlobalStyles();
  const contenedorRef = useRef(null);
  const inputRef = useRef(null);
  const [{ sesionUsuario, etiquetasIdioma }] = useStateValue();

  const usuarioId = sesionUsuario?.usuario?.userId;
  const companiaId = sesionUsuario?.usuario?.companyId;
  const idioma = etiquetasIdioma?.CodigoIdioma || "ES";

  // Labels del buscador (preferir props de BarSesion, fallback a store directo)
  const labels =
    labelsProps || etiquetasIdioma?.EtiquetasComponente?.BuscadorGlobal || {};

  // Hook de búsqueda
  const {
    textoBusqueda,
    resultados,
    cargando,
    textoPrevio,
    manejarCambioTexto,
    limpiarBusqueda,
    manejarItemClick,
    tieneResultados,
    debeMostrarNoResultados,
  } = useBusquedaGlobal({
    usuarioId,
    companiaId,
    idioma,
    onNavigate: (item, esUrlAbsoluta) => {
      if (!item.disabled && item.path) {
        if (esUrlAbsoluta) {
          window.location.href = item.path;
        } else {
          history.push(item.path);
        }
      }
    },
    onGuardarReciente: (item) => {
      if (usuarioId && item.path) {
        guardarReciente(item);
      }
    },
  });

  // Hook de recientes
  const {
    recientes,
    tieneRecientes,
    cargarRecientes,
    guardarReciente,
    eliminarReciente,
    limpiarRecientes,
  } = useRecientes(usuarioId);

  const [open, setOpen] = useState(false);
  const [resizeTrigger, setResizeTrigger] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const resizeTimeoutRef = useRef(null);

  // Efecto para clicks fuera del componente
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!open) return;
      if (inputRef.current?.contains(event.target)) return;
      if (contenedorRef.current?.contains(event.target)) return;

      manejarCierre();
    };

    const handleMouseDown = (event) => {
      if (inputRef.current?.contains(event.target)) {
        inputRef.current.dataset.clickingOpen = "true";
        setTimeout(() => {
          if (inputRef.current) {
            delete inputRef.current.dataset.clickingOpen;
          }
        }, 0);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [open]);

  // Efecto para actualizar el ancho del dropdown en resize
  useEffect(() => {
    // Listener con debounce para actualizar el trigger de re-render
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        setResizeTrigger((t) => t + 1);
      }, 300);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  // Manejadores
  const manejarEnfoque = () => {
    setOpen(true);
    cargarRecientes();
  };

  const manejarKeyDown = (event) => {
    if (event.key === "Escape" && open) {
      manejarCierre();
      inputRef.current?.blur();
    }
  };

  const manejarCierre = () => {
    setOpen(false);
    limpiarBusqueda();
  };

  const manejarMensajeSinPermisos = () => {
    setSnackbar({
      open: true,
      message:
        labels.Et_SinPermisos ||
        "No tienes permisos para acceder a este módulo",
    });
  };

  // Wrapper para manejarItemClick que incluye el mensaje de permisos
  const handleItemClick = (item) => {
    if (item.disabled) {
      manejarMensajeSinPermisos();
      return;
    }
    manejarItemClick(item);
    manejarCierre();
  };

  // Wrapper para manejarCambioTexto que reabre dropdown si está cerrado
  const handleCambioTexto = (event) => {
    manejarCambioTexto(event.target.value);

    // Si el dropdown está cerrado, reabrirlo
    if (!open) {
      setOpen(true);
    }
  };

  // Estados derivados
  const puedeMostrarRecientes = !textoBusqueda && tieneRecientes;

  // Calcular ancho del dropdown (se actualiza en resize via resizeTrigger)
  const dropdownStyleWidth = contenedorRef.current?.offsetWidth;

  return (
    <>
      <div
        className={classes.contenedor}
        ref={contenedorRef}
        onKeyDown={manejarKeyDown}
      >
        <Paper className={classes.papelInput} elevation={0}>
          <InputBase
            ref={inputRef}
            className={classes.inputBase}
            placeholder={labels.Et_Placeholder}
            value={textoBusqueda}
            onChange={handleCambioTexto}
            onFocus={manejarEnfoque}
            inputProps={{ "aria-label": "buscar" }}
          />
          {cargando ? (
            <CircularProgress
              className={classes.iconoLoading}
              size={18}
              thickness={5}
            />
          ) : (
            <SearchIcon
              className={`${classes.iconoBusqueda} ${textoBusqueda ? classes.iconoBusquedaActive : ""}`}
              fontSize="small"
            />
          )}
        </Paper>

        <Popper
          open={
            open &&
            (puedeMostrarRecientes ||
              tieneResultados ||
              textoPrevio.length >= 2)
          }
          anchorEl={contenedorRef.current}
          placement="bottom-start"
          className={classes.popper}
          modifiers={[
            { name: "flip", enabled: true },
            {
              name: "preventOverflow",
              enabled: true,
              boundariesElement: "scrollParent",
            },
          ]}
        >
          <Box
            className={classes.contenedorDropdown}
            style={{ width: dropdownStyleWidth }}
          >
            {!cargando && puedeMostrarRecientes && (
              <RecentSearches
                recientes={recientes}
                onItemClick={handleItemClick}
                onEliminarItem={eliminarReciente}
                onLimpiarTodos={limpiarRecientes}
                labels={labels}
              />
            )}

            {!cargando && tieneResultados && (
              <ResultadoLista
                resultados={resultados}
                onItemClick={handleItemClick}
                labels={labels}
              />
            )}

            {!cargando && debeMostrarNoResultados && (
              <NoResults textoBusqueda={textoPrevio} labels={labels} />
            )}
          </Box>
        </Popper>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity="warning"
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default withRouter(BuscadorGlobal);
